import type { LegalDocument } from '../../types/consent'

/**
 * Tiny markdown renderer for the limited subset our legal docs use:
 *   #/##/### headings · - list items · > blockquote · **bold** · paragraphs
 *
 * Deliberately not pulling in react-markdown — these three documents are the
 * only consumers and the surface area is small enough that a 30-line parser
 * is the right trade-off.
 */
export default function LegalDocView({ doc }: { doc: LegalDocument }) {
  const blocks = parseMarkdown(doc.content_md)
  return (
    <div className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-200">
      {blocks.map((block, i) => renderBlock(block, i))}
    </div>
  )
}

type Block =
  | { kind: 'h1' | 'h2' | 'h3'; text: string }
  | { kind: 'p'; text: string }
  | { kind: 'ul'; items: string[] }
  | { kind: 'quote'; lines: string[] }

function parseMarkdown(src: string): Block[] {
  const lines = src.split('\n')
  const blocks: Block[] = []
  let para: string[] = []
  let list: string[] = []
  let quote: string[] = []

  const flushPara = () => {
    if (para.length) {
      blocks.push({ kind: 'p', text: para.join(' ') })
      para = []
    }
  }
  const flushList = () => {
    if (list.length) {
      blocks.push({ kind: 'ul', items: list })
      list = []
    }
  }
  const flushQuote = () => {
    if (quote.length) {
      blocks.push({ kind: 'quote', lines: quote })
      quote = []
    }
  }
  const flushAll = () => {
    flushPara()
    flushList()
    flushQuote()
  }

  for (const raw of lines) {
    const line = raw.trimEnd()
    if (line === '') {
      flushAll()
      continue
    }
    if (line.startsWith('### ')) {
      flushAll()
      blocks.push({ kind: 'h3', text: line.slice(4) })
    } else if (line.startsWith('## ')) {
      flushAll()
      blocks.push({ kind: 'h2', text: line.slice(3) })
    } else if (line.startsWith('# ')) {
      flushAll()
      blocks.push({ kind: 'h1', text: line.slice(2) })
    } else if (line.startsWith('- ')) {
      flushPara()
      flushQuote()
      list.push(line.slice(2))
    } else if (line.startsWith('> ')) {
      flushPara()
      flushList()
      quote.push(line.slice(2))
    } else {
      flushList()
      flushQuote()
      para.push(line)
    }
  }
  flushAll()
  return blocks
}

function renderBlock(block: Block, key: number) {
  switch (block.kind) {
    case 'h1':
      return (
        <h2 key={key} className="text-base font-semibold text-gray-900 dark:text-white mt-4 mb-2">
          {inlineFormat(block.text)}
        </h2>
      )
    case 'h2':
      return (
        <h3 key={key} className="text-sm font-semibold text-gray-900 dark:text-white mt-4 mb-1.5">
          {inlineFormat(block.text)}
        </h3>
      )
    case 'h3':
      return (
        <h4 key={key} className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mt-3 mb-1">
          {inlineFormat(block.text)}
        </h4>
      )
    case 'p':
      return (
        <p key={key} className="text-xs leading-relaxed text-gray-600 dark:text-gray-300 mb-2">
          {inlineFormat(block.text)}
        </p>
      )
    case 'ul':
      return (
        <ul key={key} className="list-disc pl-5 mb-2 space-y-1 text-xs text-gray-600 dark:text-gray-300">
          {block.items.map((item, i) => (
            <li key={i}>{inlineFormat(item)}</li>
          ))}
        </ul>
      )
    case 'quote':
      return (
        <blockquote
          key={key}
          className="border-l-2 border-gray-200 dark:border-gray-700 pl-3 my-2 text-xs text-gray-500 dark:text-gray-400 italic"
        >
          {block.lines.map((l, i) => (
            <div key={i}>{inlineFormat(l)}</div>
          ))}
        </blockquote>
      )
  }
}

/** Handles **bold** within a line. Anything else passes through as text. */
function inlineFormat(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="font-semibold text-gray-900 dark:text-white">
          {part.slice(2, -2)}
        </strong>
      )
    }
    return <span key={i}>{part}</span>
  })
}
